"use client";

import { useTranslations } from "use-intl";

import { ExternalArrowIcon } from "@/components/appIcons";
import { WhatsAppIcon } from "@/components/icons/whatsapp-icon";
import { trackWhatsAppChannelJoinClicked } from "@/lib/analytics";
import { cn } from "@/lib/utils";

type WhatsAppFormatDestination = { invitationUrl: string } | null;

export interface WhatsAppFormatDestinations {
  vinyl?: WhatsAppFormatDestination;
  cd?: WhatsAppFormatDestination;
}

const FORMATS = ["vinyl", "cd"] as const;

export function WhatsAppFormatJoinLinks({
  destinations,
  surface,
  variant = "prominent",
}: {
  destinations?: WhatsAppFormatDestinations;
  surface: string;
  variant?: "prominent" | "compact";
}) {
  const t = useTranslations("whatsapp");
  const options = FORMATS.flatMap((format) => {
    const destination = destinations?.[format];
    return destination ? [{ format, invitationUrl: destination.invitationUrl }] : [];
  });
  if (options.length === 0) return null;

  const compact = variant === "compact";
  const showsBothFormats = options.length === 2;

  return (
    <div>
      <div
        className={cn(
          compact ? "grid gap-2" : "grid gap-3 sm:grid-cols-2",
          !compact && !showsBothFormats && "sm:grid-cols-1",
        )}
      >
        {options.map(({ format, invitationUrl }) => (
          <a
            className={cn(
              "group flex items-center gap-3 rounded-xl bg-[#25D366] font-black text-[#07170d] shadow-[0_12px_28px_rgba(37,211,102,0.22)] transition-[background-color,box-shadow,transform] hover:bg-[#36df74] hover:shadow-[0_14px_32px_rgba(37,211,102,0.3)] active:scale-[0.98] motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8bf4b1] focus-visible:ring-offset-4 focus-visible:ring-offset-[#171b18]",
              compact ? "min-h-12 justify-center px-4 py-3" : "min-h-16 px-5 py-4 text-lg",
            )}
            href={invitationUrl}
            key={format}
            onClick={() => trackWhatsAppChannelJoinClicked({ destination: format, surface })}
            rel="noopener noreferrer"
            target="_blank"
          >
            <WhatsAppIcon className={compact ? "size-5" : "size-6"} />
            <span className={cn(!compact && "min-w-0 flex-1")}>{t(`${format}GroupCta`)}</span>
            <ExternalArrowIcon
              className={cn(
                "shrink-0 text-[#07170d]/70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transform-none",
                compact ? "text-base" : "text-xl",
              )}
              decorative
            />
          </a>
        ))}
      </div>
      {showsBothFormats && (
        <p
          className={cn(
            "text-sm leading-6",
            compact ? "mt-3 text-[#aaa79f]" : "mt-4 text-[#999f99]",
          )}
        >
          {t("bothFormatsNote")}
        </p>
      )}
    </div>
  );
}
